/**
 * Founder UAT (A) — Client-portal management of the Phase 3 Position model.
 * Person → Position → Role Profile, operated by Client Admin / Client HR.
 * Explicit Role Profile selection (no name matching). Multiple occupants,
 * derived vacancy (authorized − filled). Organizational only — no RBAC.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Briefcase, Plus, Pencil, Archive, UserPlus, X, Link2, ArrowLeft } from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { useDataLabel } from '@/lib/dataI18n';

const NONE = '__none__';

export default function ClientDeptPositions({ open, onOpenChange, department, canManage, onChanged }) {
  const { t } = useTranslation();
  const tData = useDataLabel();
  const [positions, setPositions] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [mode, setMode] = useState('list'); // list | form
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', role_profile_id: NONE, headcount: '' });
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    if (!department?.id) return;
    try {
      const [pos, rps, emps] = await Promise.all([
        api.get('/client/positions', { params: { department_id: department.id } }).then((r) => r.data),
        api.get('/client/role-profiles').then((r) => r.data).catch(() => []),
        api.get('/client/employees', { params: { include_admins: 1 } }).then((r) => r.data).catch(() => []),
      ]);
      setPositions(pos || []);
      setProfiles(rps || []);
      setEmployees(emps || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => {
    if (open) { setMode('list'); reload(); }
    // eslint-disable-next-line
  }, [open, department?.id]);

  const companyProfiles = useMemo(() => profiles.filter((p) => p.scope === 'company'), [profiles]);
  const globalProfiles = useMemo(() => profiles.filter((p) => p.scope === 'global'), [profiles]);
  const suggestions = department?.positions || [];

  const openNew = () => { setEditing(null); setForm({ title: '', role_profile_id: NONE, headcount: '' }); setMode('form'); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ title: p.title, role_profile_id: p.role_profile_id || NONE, headcount: p.headcount ? String(p.headcount) : '' });
    setMode('form');
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error(t('phase3.positions.titleRequired', { defaultValue: 'Position title is required' })); return; }
    setSaving(true);
    const body = {
      title: form.title.trim(),
      role_profile_id: form.role_profile_id === NONE ? null : form.role_profile_id,
      headcount: form.headcount ? parseInt(form.headcount, 10) : null,
    };
    try {
      if (editing) await api.patch(`/client/positions/${editing.id}`, body);
      else await api.post('/client/positions', { ...body, department_id: department.id });
      toast.success(t('common.saved', { defaultValue: 'Saved' }));
      setMode('list');
      reload();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const archivePosition = async (p) => {
    try {
      await api.patch(`/client/positions/${p.id}`, { archived: true });
      toast.success(t('common.archived', { defaultValue: 'Archived' }));
      reload(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const assign = async (p, userId) => {
    try {
      await api.post(`/client/positions/${p.id}/occupants`, { user_id: userId });
      toast.success(t('phase3.positions.occupantAssigned', { defaultValue: 'Occupant assigned' }));
      reload(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeOcc = async (p, userId) => {
    try {
      await api.delete(`/client/positions/${p.id}/occupants/${userId}`);
      toast.success(t('phase3.positions.occupantRemoved', { defaultValue: 'Occupant removed (history preserved)' }));
      reload(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[86vh] overflow-y-auto" data-testid="client-positions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-primary" />
            {t('phase3.positionsClient.dialogTitle', { defaultValue: 'Positions — {{dept}}', dept: department ? tData(department) : '' })}
          </DialogTitle>
          <DialogDescription>
            {t('phase3.positionsClient.dialogDesc', { defaultValue: 'Each position may link a formal Role Profile and hold one or more occupants. Vacant seats are derived: authorized seats minus filled seats. This never grants platform permissions.' })}
          </DialogDescription>
        </DialogHeader>

        {mode === 'list' && (
          <div className="space-y-3">
            {canManage && (
              <div className="flex justify-end">
                <Button size="sm" onClick={openNew} className="bg-gradient-primary gap-1.5" data-testid="client-position-new-btn">
                  <Plus className="h-3.5 w-3.5" />{t('phase3.positions.new', { defaultValue: 'New position' })}
                </Button>
              </div>
            )}
            {!positions.length ? (
              <p className="text-sm text-muted-foreground italic py-8 text-center" data-testid="client-positions-empty">
                {t('phase3.positionsClient.empty', { defaultValue: 'No formal positions defined in this department yet. Create the first one (e.g. "Director General") and link its Role Profile.' })}
              </p>
            ) : positions.map((p) => {
              const seats = p.headcount || null;
              const vacant = seats ? Math.max(0, seats - p.occupied) : null;
              return (
                <div key={p.id} className="border border-border rounded-lg p-3.5" data-testid={`client-position-row-${p.id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{p.title}</span>
                        {p.role_profile ? (
                          <Badge variant="outline" className="text-xs gap-1 bg-primary/5 border-primary/30 text-primary" data-testid={`client-position-rp-${p.id}`}>
                            <Link2 className="h-3 w-3" />{p.role_profile.position_title}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-muted-foreground">
                            {t('phase3.positions.noProfile', { defaultValue: 'No role profile' })}
                          </Badge>
                        )}
                        <Badge variant="secondary" className="text-xs tabular-nums" data-testid={`client-position-seats-${p.id}`}>
                          {seats
                            ? t('phase3.seats.summaryShort', { defaultValue: '{{filled}}/{{total}} seats', filled: p.occupied, total: seats })
                            : `${p.occupied} ${t('phase3.positions.occupantsLabel', { defaultValue: 'occupant(s)' })}`}
                        </Badge>
                        {seats != null && vacant > 0 && (
                          <Badge className="bg-warning/10 text-warning border-warning/30 text-xs" variant="outline">
                            {vacant} {t('phase3.seats.vacantShort', { defaultValue: 'vacant' })}
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {(p.occupants || []).map((o) => (
                          <Badge key={o.id} variant="outline" className="text-xs gap-1 pr-1" data-testid={`client-position-occ-${p.id}-${o.id}`}>
                            {o.name}
                            {canManage && (
                              <button type="button"
                                className="ml-0.5 rounded-full hover:bg-destructive/10 hover:text-destructive p-0.5"
                                onClick={() => removeOcc(p, o.id)}
                                data-testid={`client-position-occ-remove-${p.id}-${o.id}`}
                                title={t('phase3.positions.removeOccupant', { defaultValue: 'Remove occupant (history preserved)' })}>
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </Badge>
                        ))}
                        {!(p.occupants || []).length && (
                          <span className="text-xs text-muted-foreground italic">{t('phase3.positionsClient.noOccupants', { defaultValue: 'Vacant — no occupants yet' })}</span>
                        )}
                      </div>
                    </div>
                    {canManage && (
                      <div className="flex items-center gap-2">
                        <Select value="" onValueChange={(v) => assign(p, v)}>
                          <SelectTrigger className="h-8 text-xs w-52" data-testid={`client-position-assign-${p.id}`}>
                            <span className="flex items-center gap-1 text-muted-foreground">
                              <UserPlus className="h-3.5 w-3.5" />
                              {t('phase3.positions.assignOccupant', { defaultValue: 'Assign occupant' })}
                            </span>
                          </SelectTrigger>
                          <SelectContent>
                            {employees.filter((e) => !(p.occupants || []).some((o) => o.id === e.id)).map((e) => (
                              <SelectItem key={e.id} value={e.id}>
                                {e.name}{e.position ? ` · ${e.position}` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openEdit(p)} data-testid={`client-position-edit-${p.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-muted-foreground" onClick={() => archivePosition(p)} data-testid={`client-position-archive-${p.id}`}>
                          <Archive className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {mode === 'form' && (
          <div className="space-y-4">
            <Button size="sm" variant="ghost" className="gap-1 -ml-2" onClick={() => setMode('list')} data-testid="client-position-back">
              <ArrowLeft className="h-3.5 w-3.5" />{t('common.back', { defaultValue: 'Back' })}
            </Button>
            <div>
              <Label>{t('phase3.positions.titleLabel', { defaultValue: 'Position title' })}</Label>
              <Input className="mt-1.5" value={form.title} list="client-pos-suggestions"
                     placeholder={t('phase3.positionsClient.titlePh', { defaultValue: 'e.g. Director General, Asistente de Bodega' })}
                     onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="client-position-title-input" />
              {suggestions.length > 0 && (
                <>
                  <datalist id="client-pos-suggestions">
                    {suggestions.map((s, i) => <option key={i} value={s} />)}
                  </datalist>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('phase3.positionsClient.suggestionsHint', { defaultValue: 'Suggestions come from this department\u2019s reference catalogue — you can type any real title.' })}
                  </p>
                </>
              )}
            </div>
            <div>
              <Label>{t('phase3.positionsClient.roleProfileLabel', { defaultValue: 'Formal Role Profile (explicit link — optional)' })}</Label>
              <Select value={form.role_profile_id} onValueChange={(v) => setForm({ ...form, role_profile_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="client-position-rp-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t('phase3.positions.noProfile', { defaultValue: 'No role profile' })}</SelectItem>
                  {companyProfiles.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>{t('phase3.positions.companyProfiles', { defaultValue: 'Company profiles' })}</SelectLabel>
                      {companyProfiles.map((rp) => <SelectItem key={rp.id} value={rp.id}>{rp.position_title}</SelectItem>)}
                    </SelectGroup>
                  )}
                  {globalProfiles.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>{t('phase3.positions.globalProfiles', { defaultValue: 'Global CTH templates' })}</SelectLabel>
                      {globalProfiles.map((rp) => <SelectItem key={rp.id} value={rp.id}>{rp.position_title}</SelectItem>)}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {t('phase3.positionsClient.roleProfileHint', { defaultValue: 'The occupant is evaluated against this formal profile. Selection is always explicit — never matched by name.' })}
              </p>
            </div>
            <div>
              <Label>{t('phase3.seats.authorizedOptional', { defaultValue: 'Authorized seats (optional)' })}</Label>
              <Input className="mt-1.5 w-40" type="number" min={1} value={form.headcount}
                     onChange={(e) => setForm({ ...form, headcount: e.target.value })}
                     placeholder={t('phase3.positions.headcountPh', { defaultValue: 'e.g. 5' })}
                     data-testid="client-position-headcount-input" />
              <p className="text-xs text-muted-foreground mt-1">
                {t('phase3.seats.derivedHint', { defaultValue: 'Vacant seats are always derived: authorized seats − filled seats.' })}
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setMode('list')}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
              <Button onClick={save} disabled={saving} className="bg-gradient-primary" data-testid="client-position-save-btn">
                {saving ? t('common.saving', { defaultValue: 'Saving…' }) : t('common.save', { defaultValue: 'Save' })}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
